'use client'

import { useState, ReactNode, useEffect, useCallback, useRef } from 'react'
import { Menu, X, Search, Command, ChevronDown, Plus, Bell, User, Settings, Home, Folder, Star, Archive } from 'lucide-react'
import { 
  borderRadiusV3, 
  spacingV3, 
  shadowsV3, 
  animationsV3, 
  glassV3,
  componentSpacing,
  componentAnimations,
  componentShadows,
  componentGlass,
  typographyV3,
  componentTypography
} from '@birhaus/tokens/v3'
import { cn, ClassValue } from '@birhaus/types/utils'

/**
 * BIRHAUS Navigation V3 - Radical Minimalist Navigation
 * 
 * Revolutionary navigation component with:
 * - Glass morphism nav bars with backdrop blur
 * - Smooth tab transitions and hover effects
 * - Floating action buttons integration
 * - Radical border radius (24px default)
 * - Generous spacing (2x traditional)
 * - BIRHAUS 4-3-1 rule enforcement
 * - Spanish-first labeling
 * - Performance optimized animations
 */

// Using imported cn utility from @birhaus/types/utils

export interface NavigationItemV3 {
  id: string
  label: string
  labelEs?: string
  labelEn?: string
  icon?: ReactNode
  href?: string
  onClick?: () => void
  active?: boolean
  disabled?: boolean
  badge?: string | number
  badgeColor?: 'blue' | 'red' | 'green' | 'yellow' | 'purple'
  submenu?: NavigationItemV3[]
  
  // V3 enhancements
  glow?: boolean
  glass?: boolean
  priority?: 'high' | 'medium' | 'low'
}

export interface ContextualActionV3 {
  id: string
  label: string
  labelEs?: string
  labelEn?: string
  icon?: ReactNode
  onClick: () => void
  variant?: 'default' | 'secondary' | 'destructive' | 'glass' | 'ghost'
  disabled?: boolean
  loading?: boolean
  
  // V3 enhancements
  glow?: boolean
  pulse?: boolean
  tooltip?: string
}

export interface PrimaryActionV3 {
  label: string
  labelEs?: string
  labelEn?: string
  icon?: ReactNode
  onClick: () => void
  disabled?: boolean
  loading?: boolean
  
  // V3 enhancements
  glow?: boolean
  pulse?: boolean
  float?: boolean
  variant?: 'primary' | 'glass' | 'gradient'
}

export interface BirhausNavigationV3Props {
  // BIRHAUS: Maximum 4 navigation items (4-3-1 rule)
  navigationItems: NavigationItemV3[]
  
  // BIRHAUS: Maximum 3 contextual actions
  contextualActions?: ContextualActionV3[]
  
  // BIRHAUS: 1 primary action
  primaryAction?: PrimaryActionV3
  
  // Branding
  logo?: ReactNode
  logoHref?: string
  onLogoClick?: () => void
  
  // V3 Design variants
  variant?: 'default' | 'glass' | 'frosted' | 'minimal' | 'floating'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  radius?: keyof typeof borderRadiusV3
  position?: 'top' | 'bottom' | 'left' | 'right'
  
  // V3 Visual effects
  glassMorphism?: boolean
  glow?: boolean
  float?: boolean
  blur?: number
  backdropBlur?: number
  
  // Search functionality
  searchPlaceholder?: string
  searchPlaceholderEs?: string
  searchPlaceholderEn?: string
  onSearchClick?: () => void
  showSearch?: boolean
  
  // Command palette
  showCommandPalette?: boolean
  onCommandPaletteToggle?: () => void
  commandPaletteShortcut?: string
  
  // Behavior
  collapsible?: boolean
  autoCollapse?: boolean
  collapseBreakpoint?: 'sm' | 'md' | 'lg'
  
  // Mobile
  mobileBreakpoint?: 'sm' | 'md' | 'lg'
  
  // Overflow behavior
  overflowBehavior?: 'collapse' | 'scroll' | 'dropdown'
  overflowLabel?: string
  overflowLabelEs?: string
  overflowLabelEn?: string
  
  // Labels (Spanish-first)
  menuLabel?: string
  menuLabelEs?: string
  menuLabelEn?: string
  closeMenuLabel?: string
  searchLabel?: string
  commandPaletteLabel?: string
  
  // Performance
  trackPerformance?: boolean
  
  // Styling
  className?: string
  containerClassName?: string
}

export const BirhausNavigationV3: React.FC<BirhausNavigationV3Props> = ({
  navigationItems,
  contextualActions = [],
  primaryAction,
  
  // Branding
  logo,
  logoHref,
  onLogoClick,
  
  // V3 Design
  variant = 'glass',
  size = 'md',
  radius = 'xl',
  position = 'top',
  
  // V3 Effects
  glassMorphism = true,
  glow = false,
  float = true,
  blur = 16,
  backdropBlur = 24,
  
  // Search
  searchPlaceholder,
  searchPlaceholderEs,
  searchPlaceholderEn,
  onSearchClick,
  showSearch = true,
  
  // Command palette
  showCommandPalette = true,
  onCommandPaletteToggle,
  commandPaletteShortcut = '⌘K',
  
  // Behavior
  collapsible = true,
  autoCollapse = false,
  collapseBreakpoint = 'md',
  
  // Mobile
  mobileBreakpoint = 'md',
  
  // Overflow
  overflowBehavior = 'collapse',
  overflowLabel,
  overflowLabelEs,
  overflowLabelEn,
  
  // Labels
  menuLabel = 'Menú',
  menuLabelEs,
  menuLabelEn,
  closeMenuLabel = 'Cerrar menú',
  searchLabel = 'Buscar',
  commandPaletteLabel = 'Abrir paleta de comandos',
  
  // Performance
  trackPerformance = false,
  
  // Styling
  className,
  containerClassName,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [activeItem, setActiveItem] = useState<string>()
  const [isCollapsed, setIsCollapsed] = useState(autoCollapse)
  const [hoveredItem, setHoveredItem] = useState<string>()
  
  const navRef = useRef<HTMLElement>(null)
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const resolveText = (spanish?: string, english?: string, fallback?: string) => {
    return spanish || english || fallback || ''
  }
  
  const displaySearchPlaceholder = resolveText(
    searchPlaceholderEs,
    searchPlaceholderEn,
    searchPlaceholder || 'Buscar...'
  )
  
  const displayOverflowLabel = resolveText(
    overflowLabelEs,
    overflowLabelEn,
    overflowLabel || 'Más opciones'
  )
  
  const displayMenuLabel = resolveText(
    menuLabelEs,
    menuLabelEn,
    menuLabel
  )
  
  // BIRHAUS: Validate 4-3-1 rule
  useEffect(() => {
    if (navigationItems.length > 4) {
      console.warn(`[BIRHAUS Navigation V3] Violating 4-3-1 rule: ${navigationItems.length} navigation items (max 4). Consider using progressive disclosure.`)
    }
    
    if (contextualActions.length > 3) {
      console.warn(`[BIRHAUS Navigation V3] Violating 4-3-1 rule: ${contextualActions.length} contextual actions (max 3). Consider consolidating actions.`)
    }
    
    const primaryActions = contextualActions.filter(action => action.variant === 'default').length + (primaryAction ? 1 : 0)
    if (primaryActions > 1) {
      console.warn(`[BIRHAUS Navigation V3] Violating 4-3-1 rule: ${primaryActions} primary actions (max 1). Ensure only one primary action is highlighted.`)
    }
  }, [navigationItems, contextualActions, primaryAction])
  
  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        onCommandPaletteToggle?.()
      }
      
      if (e.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false)
      }
    }
    
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onCommandPaletteToggle, isMobileMenuOpen])
  
  // Auto-collapse on scroll
  useEffect(() => {
    if (!autoCollapse) return
    
    const handleScroll = () => {
      if (window.scrollY > 100 && !isCollapsed) {
        setIsCollapsed(true)
      } else if (window.scrollY <= 100 && isCollapsed) {
        setIsCollapsed(false)
      }
    }
    
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [autoCollapse, isCollapsed])
  
  // V3 Size classes with generous spacing
  const sizeClasses = {
    sm: 'h-16 px-6',
    md: 'h-20 px-8',
    lg: 'h-24 px-10',
    xl: 'h-32 px-12',
  }
  
  // V3 Variant classes
  const variantClasses = {
    default: 'bg-white/95 border-b border-gray-200/50',
    glass: `${componentGlass.navigation} backdrop-blur-xl border-b border-white/20`,
    frosted: `${glassV3.effects.heavy} backdrop-blur-2xl border-b border-white/30`,
    minimal: 'bg-transparent',
    floating: `
      ${componentGlass.navigation}
      backdrop-blur-xl
      rounded-[${borderRadiusV3[radius]}]
      mx-6 mt-6 mb-0
      border border-white/20
      shadow-elevation-3
    `,
  }
  
  // Position classes
  const positionClasses = {
    top: 'top-0 left-0 right-0',
    bottom: 'bottom-0 left-0 right-0',
    left: 'top-0 left-0 bottom-0 w-20',
    right: 'top-0 right-0 bottom-0 w-20',
  }
  
  // Handle navigation item click
  const handleNavItemClick = (item: NavigationItemV3) => {
    if (item.disabled) return
    
    setActiveItem(item.id)
    
    if (item.onClick) {
      item.onClick()
    } else if (item.href) {
      window.location.href = item.href
    }
    
    // Close mobile menu
    setIsMobileMenuOpen(false)
  }
  
  // Handle contextual action click
  const handleActionClick = (action: ContextualActionV3) => {
    if (action.disabled) return
    action.onClick()
  }
  
  // Badge colors
  const badgeColors = {
    blue: 'bg-blue-500 text-white',
    red: 'bg-red-500 text-white',
    green: 'bg-green-500 text-white',
    yellow: 'bg-yellow-500 text-white',
    purple: 'bg-purple-500 text-white',
  }
  
  // Render navigation item
  const renderNavItem = (item: NavigationItemV3, isMobile = false) => {
    const isActive = item.active || activeItem === item.id
    const isHovered = hoveredItem === item.id
    const displayLabel = resolveText(item.labelEs, item.labelEn, item.label)
    
    return (
      <button
        key={item.id}
        onClick={() => handleNavItemClick(item)}
        onMouseEnter={() => setHoveredItem(item.id)}
        onMouseLeave={() => setHoveredItem(undefined)}
        disabled={item.disabled}
        className={cn(
          // Base styles
          'relative flex items-center gap-3',
          isMobile ? 'w-full px-6 py-4 text-left' : 'px-6 py-3',
          'font-medium transition-all duration-200',
          'focus:outline-none focus:ring-2 focus:ring-blue-500/20',
          `rounded-[${borderRadiusV3.md}]`,
          
          // States
          isActive && [
            'text-blue-600',
            glassMorphism && 'bg-white/50',
            !glassMorphism && 'bg-blue-50',
            'shadow-sm',
          ],
          !isActive && [
            'text-gray-700',
            'hover:text-gray-900',
            glassMorphism && 'hover:bg-white/30',
            !glassMorphism && 'hover:bg-gray-100/50',
          ],
          
          // V3 Effects
          item.glow && isActive && 'shadow-[0_0_20px_-6px_rgba(59,130,246,0.4)]',
          item.glass && 'bg-white/30 backdrop-blur-md saturate-[150%] border border-white/20 shadow-lg',
          float && 'hover:scale-105 transform-gpu',
          
          // Disabled state
          item.disabled && 'opacity-50 cursor-not-allowed',
          
          // Mobile styles
          isMobile && 'justify-start'
        )}
        title={displayLabel}
      >
        {/* Icon */}
        {item.icon && (
          <span className={cn(
            'flex-shrink-0',
            isActive ? 'text-blue-600' : 'text-gray-500',
            item.glow && isActive && 'filter drop-shadow-sm'
          )}>
            {item.icon}
          </span>
        )}
        
        {/* Label */}
        <span className={cn(
          'text-sm font-medium',
          isMobile && 'text-base'
        )}>
          {displayLabel}
        </span>
        
        {/* Badge */}
        {item.badge && (
          <span className={cn(
            'inline-flex items-center justify-center',
            'min-w-[20px] h-5 px-2 text-xs font-bold rounded-full',
            badgeColors[item.badgeColor || 'blue'],
            'animate-pulse'
          )}>
            {item.badge}
          </span>
        )}
        
        {/* Submenu indicator */}
        {item.submenu && item.submenu.length > 0 && (
          <ChevronDown size={16} className={cn(
            'ml-auto transition-transform duration-200',
            isHovered && 'rotate-180'
          )} />
        )}
        
        {/* Active indicator */}
        {isActive && (
          <span className={cn(
            'absolute bottom-0 left-1/2 transform -translate-x-1/2',
            'w-8 h-0.5 bg-blue-600 rounded-full',
            componentAnimations.activeIndicator
          )} />
        )}
      </button>
    )
  }
  
  // Render contextual action
  const renderContextualAction = (action: ContextualActionV3) => {
    const displayLabel = resolveText(action.labelEs, action.labelEn, action.label)
    
    return (
      <button
        key={action.id}
        onClick={() => handleActionClick(action)}
        disabled={action.disabled || action.loading}
        className={cn(
          // Base styles
          'relative flex items-center gap-2',
          'px-4 py-2',
          'font-medium transition-all duration-200',
          'focus:outline-none focus:ring-2 focus:ring-blue-500/20',
          `rounded-[${borderRadiusV3.md}]`,
          
          // Variant styles
          action.variant === 'destructive' && [
            'text-red-600 hover:text-red-700',
            'hover:bg-red-50',
          ],
          action.variant === 'glass' && [
            'bg-white/30 backdrop-blur-md saturate-[150%] border border-white/20 shadow-lg',
            'text-gray-700 hover:bg-white/60',
          ],
          (!action.variant || action.variant === 'secondary') && [
            'text-gray-600 hover:text-gray-800',
            'hover:bg-gray-100/50',
          ],
          action.variant === 'default' && [
            'text-blue-600 hover:text-blue-700',
            'hover:bg-blue-50',
          ],
          
          // Effects
          action.glow && 'shadow-[0_0_16px_-6px_rgba(59,130,246,0.3)]',
          action.pulse && 'animate-pulse',
          
          // Disabled state
          (action.disabled || action.loading) && 'opacity-50 cursor-not-allowed'
        )}
        title={action.tooltip || displayLabel}
      >
        {action.icon && (
          <span className="flex-shrink-0">
            {action.icon}
          </span>
        )}
        
        <span className={cn(
          'text-sm font-medium',
          'hidden sm:inline'
        )}>
          {displayLabel}
        </span>
      </button>
    )
  }
  
  return (
    <nav
      ref={navRef}
      className={cn(
        // Base styles
        'fixed z-40 transition-all duration-300',
        sizeClasses[size],
        positionClasses[position],
        variantClasses[variant],
        
        // Collapse animation
        isCollapsed && autoCollapse && 'transform -translate-y-2 scale-95 opacity-90',
        
        // Glow effect
        glow && 'shadow-lg hover:shadow-xl',
        
        // Performance optimizations
        'transform-gpu will-change-transform backface-visibility-hidden',
        
        containerClassName
      )}
    >
      <div className={cn(
        'flex items-center justify-between h-full',
        'max-w-7xl mx-auto',
        className
      )}>
        {/* Logo Section */}
        <div className="flex items-center gap-6">
          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={cn(
              `${mobileBreakpoint}:hidden`,
              'p-2 rounded-lg',
              'text-gray-600 hover:text-gray-800',
              'hover:bg-gray-100/50',
              'transition-colors duration-200',
              'focus:outline-none focus:ring-2 focus:ring-blue-500/20'
            )}
            aria-label={isMobileMenuOpen ? closeMenuLabel : displayMenuLabel}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          
          {/* Logo */}
          {logo && (
            <div className="flex items-center">
              {logoHref ? (
                <a href={logoHref} className="flex items-center">
                  {logo}
                </a>
              ) : onLogoClick ? (
                <button onClick={onLogoClick} className="flex items-center">
                  {logo}
                </button>
              ) : (
                logo
              )}
            </div>
          )}
          
          {/* Desktop Navigation */}
          <div className={cn(
            `hidden ${mobileBreakpoint}:flex`,
            'items-center gap-2'
          )}>
            {navigationItems.slice(0, 4).map(item => renderNavItem(item))}
            
            {/* Overflow menu */}
            {navigationItems.length > 4 && overflowBehavior === 'dropdown' && (
              <div className="relative">
                <button className={cn(
                  'flex items-center gap-2 px-4 py-2',
                  'text-gray-600 hover:text-gray-800',
                  'hover:bg-gray-100/50',
                  'rounded-xl transition-colors duration-200'
                )}>
                  <span>{displayOverflowLabel}</span>
                  <ChevronDown size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Actions Section */}
        <div className="flex items-center gap-3">
          {/* Search */}
          {showSearch && onSearchClick && (
            <button
              onClick={onSearchClick}
              className={cn(
                'hidden sm:flex items-center gap-3',
                'px-4 py-2 rounded-xl',
                'text-gray-500 hover:text-gray-700',
                glassMorphism && 'bg-white/30 hover:bg-white/50',
                !glassMorphism && 'bg-gray-100/50 hover:bg-gray-100',
                'border border-gray-200/50',
                'transition-all duration-200',
                'focus:outline-none focus:ring-2 focus:ring-blue-500/20',
                'min-w-[200px] justify-start'
              )}
            >
              <Search size={16} />
              <span className="text-sm">{displaySearchPlaceholder}</span>
            </button>
          )}
          
          {/* Command Palette */}
          {showCommandPalette && onCommandPaletteToggle && (
            <button
              onClick={onCommandPaletteToggle}
              className={cn(
                'hidden md:flex items-center gap-2',
                'px-3 py-2 rounded-xl',
                'text-gray-500 hover:text-gray-700',
                glassMorphism && 'bg-white/30 hover:bg-white/50',
                !glassMorphism && 'bg-gray-100/50 hover:bg-gray-100',
                'border border-gray-200/50',
                'transition-all duration-200',
                'focus:outline-none focus:ring-2 focus:ring-blue-500/20'
              )}
              title={commandPaletteLabel}
            >
              <Command size={16} />
              <span className="text-xs font-mono">{commandPaletteShortcut}</span>
            </button>
          )}
          
          {/* Contextual Actions */}
          <div className="flex items-center gap-2">
            {contextualActions.slice(0, 3).map(renderContextualAction)}
          </div>
          
          {/* Primary Action */}
          {primaryAction && (
            <button
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled || primaryAction.loading}
              className={cn(
                // Base styles
                'flex items-center gap-2',
                'px-6 py-3',
                'font-semibold transition-all duration-200',
                'focus:outline-none focus:ring-2 focus:ring-blue-500/20',
                `rounded-[${borderRadiusV3.lg}]`,
                
                // Variant styles
                primaryAction.variant === 'glass' && [
                  'bg-white/50 backdrop-blur-lg saturate-[150%] border border-white/30 shadow-xl',
                  'text-gray-900 hover:bg-white/70',
                  'border border-white/30',
                ],
                primaryAction.variant === 'gradient' && [
                  'bg-gradient-to-r from-blue-600 to-purple-600',
                  'text-white hover:from-blue-700 hover:to-purple-700',
                  'shadow-lg hover:shadow-xl',
                ],
                (!primaryAction.variant || primaryAction.variant === 'primary') && [
                  'bg-blue-600 text-white',
                  'hover:bg-blue-700',
                  'shadow-lg hover:shadow-xl',
                ],
                
                // Effects
                primaryAction.glow && 'shadow-[0_0_24px_-6px_rgba(59,130,246,0.4)]',
                primaryAction.pulse && 'animate-pulse',
                primaryAction.float && 'hover:scale-105 transform-gpu',
                
                // Disabled state
                (primaryAction.disabled || primaryAction.loading) && 'opacity-50 cursor-not-allowed'
              )}
            >
              {primaryAction.icon && !primaryAction.loading && (
                <span className="flex-shrink-0">
                  {primaryAction.icon}
                </span>
              )}
              
              {primaryAction.loading && (
                <span className="flex-shrink-0 animate-spin">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
                </span>
              )}
              
              <span className="hidden sm:inline">
                {resolveText(primaryAction.labelEs, primaryAction.labelEn, primaryAction.label)}
              </span>
            </button>
          )}
        </div>
      </div>
      
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className={cn(
            `${mobileBreakpoint}:hidden`,
            'fixed inset-0 z-50 transition-opacity duration-300',
            'bg-black/20 backdrop-blur-sm'
          )}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div
            className={cn(
              'absolute top-20 left-0 right-0 mx-6',
              'p-6 rounded-2xl',
              glassMorphism ? [
                'bg-white/70 backdrop-blur-2xl saturate-[180%] border border-white/40 shadow-2xl',
                'backdrop-blur-xl border border-white/20',
              ] : [
                'bg-white',
                'border border-gray-200/50',
                'shadow-xl',
              ],
              'transition-all duration-300 ease-in-out animate-slide-down'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-2">
              {navigationItems.map(item => renderNavItem(item, true))}
            </div>
            
            {/* Mobile search */}
            {showSearch && onSearchClick && (
              <div className="pt-6 mt-6 border-t border-gray-200/50">
                <button
                  onClick={onSearchClick}
                  className={cn(
                    'w-full flex items-center gap-3',
                    'px-4 py-3 rounded-xl',
                    'text-gray-600 hover:text-gray-800',
                    'bg-gray-100/50 hover:bg-gray-100',
                    'transition-colors duration-200'
                  )}
                >
                  <Search size={20} />
                  <span>{displaySearchPlaceholder}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  )
}

BirhausNavigationV3.displayName = 'BirhausNavigationV3'

/**
 * Hook for managing navigation state with v3 enhancements
 */
export const useBirhausNavigationV3 = () => {
  const [activeItem, setActiveItem] = useState<string>()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false)
  
  const setActive = (itemId: string) => setActiveItem(itemId)
  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen)
  const closeMobileMenu = () => setIsMobileMenuOpen(false)
  const toggleCollapsed = () => setIsCollapsed(!isCollapsed)
  
  return {
    activeItem,
    isMobileMenuOpen,
    isCollapsed,
    setActive,
    toggleMobileMenu,
    closeMobileMenu,
    toggleCollapsed,
  }
}

/**
 * Helper to create navigation items with v3 styling
 */
export const createBirhausNavigationV3Item = (
  item: Omit<NavigationItemV3, 'id'>
): NavigationItemV3 => ({
  id: crypto.randomUUID(),
  ...item,
})